import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { playSound, DEFAULT_PREFS } from "../lib/sounds";

export default function NotificationBell() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const seen = useRef(null);
  const prefs = useRef(DEFAULT_PREFS);
  prefs.current = { ...DEFAULT_PREFS, ...(user?.sound_prefs || {}) };

  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => {
      const latest = r.data.items[0]?.created_at || "";
      if (seen.current !== null) {
        const fresh = r.data.items.filter((n) => !n.read && n.created_at > seen.current);
        if (fresh.length) {
          const kind = fresh.some((n) => (n.kind || "order") === "order") ? "order" : "message";
          playSound(prefs.current[kind], prefs.current.volume);
        }
      }
      if (seen.current === null || latest > seen.current) seen.current = latest;
      setData(r.data);
    }).catch(() => {});
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, []);

  const toggle = () => {
    setOpen(!open);
    if (!open && data.unread) api.post("/notifications/read-all").then(() => setData((d) => ({ ...d, unread: 0 })));
  };

  return (
    <div className="relative">
      <button data-testid="nav-notifications" onClick={toggle} className="relative flex items-center text-[#F5F3ED] hover:text-[#D4AF37] transition-colors px-2 py-2" aria-label={t("notif.title")}>
        <Bell size={18} />
        {data.unread > 0 && <span data-testid="notifications-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-[#101114] text-[10px] font-bold flex items-center justify-center">{data.unread}</span>}
      </button>
      {open && (
        <div data-testid="notifications-dropdown" className="fixed left-2 right-2 top-20 sm:absolute sm:left-auto sm:top-auto sm:right-0 mt-2 sm:w-80 max-h-96 overflow-y-auto bg-[#202127] border border-[#35353B] rounded-lg shadow-2xl z-50">
          <div className="px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] border-b border-[#35353B]">{t("notif.title")}</div>
          {data.items.map((n) => (
            <Link key={n.id} to={n.kind === "message" && n.conversation_id ? `/chat?c=${n.conversation_id}` : n.link} onClick={() => setOpen(false)} data-testid={`notification-${n.id}`}
              className={`block px-4 py-3 border-b border-[#35353B] hover:bg-[#191A1F] ${n.read ? "" : "bg-[#D4AF37]/5"}`}>
              <div className="text-sm text-[#F5F3ED] font-semibold">{n.title}</div>
              <div className="text-xs text-[#A8A8AD] whitespace-pre-wrap break-words mt-0.5">{n.body}</div>
              <div className="text-[10px] text-[#A8A8AD] mt-1">{new Date(n.created_at).toLocaleString()}</div>
            </Link>
          ))}
          {data.items.length === 0 && <div className="px-4 py-6 text-center text-sm text-[#A8A8AD]">{t("notif.empty")}</div>}
        </div>
      )}
    </div>
  );
}
